import i18n from "i18next";
import { IconBook, IconFileText, IconId, IconReport } from "@tabler/icons-react";
import type { ComponentType } from "react";
import type {
  CategoryTilePickerProps,
  DocumentCategory,
} from "@/types/documentClass";
import styles from "./CategoryTilePicker.module.scss";

interface TileSpec {
  category: DocumentCategory;
  label: string;
  Icon: ComponentType<{ size?: number | string; stroke?: number | string }>;
}

/**
 * Categories offered when creating or restamping a document.
 *
 * Narrowed to Article + Report on 2026-09-06. The corpus evidence is in
 * P1.5 (lilia-docs/plan/PLAN.md): article is 66.3% and rising, book/report
 * counts as ONE category at 19.3% and flat — 85.6% between them — while cv
 * is 1.7% and falling. `report` rather than `book` because it gives chapters
 * without committing to front/back matter, which is the work `book` would
 * add and which no emitter has yet.
 *
 * `book` and `cv` stay in the DocumentCategory union on purpose. Documents
 * already carry those values, the C# CreateDocumentDto mirrors the union
 * 1:1, and this module's own contract is append-only. Removing them would
 * break existing documents to tidy a picker. So they are hidden from the
 * choice, exactly as CV and slide BLOCKS were hidden rather than deleted
 * (#228, #230) — and a document that already is one keeps rendering,
 * exporting and stamping as one.
 *
 * Restoring a tile is one line here.
 */
const TILES: ReadonlyArray<TileSpec> = [
  { category: "article", label: i18n.t("shell:categories.article"), Icon: IconFileText },
  { category: "report", label: i18n.t("shell:categories.report"), Icon: IconReport },
];

/**
 * Hidden from the choice, still representable — see TILES.
 *
 * A document that already IS one of these still has to show what it is: with
 * only Article and Report on screen, a book would render with nothing selected
 * and look like it had lost its type. So the current value gets a tile even
 * when it is out of scope. It appears because the document is already that,
 * never as an option for a new one.
 */
const DESCOPED: ReadonlyArray<TileSpec> = [
  { category: "book", label: i18n.t("shell:categories.book"), Icon: IconBook },
  { category: "cv", label: "CV", Icon: IconId },
];

/**
 * Categories that have more than one canonical class in scope and
 * therefore deserve a "Change" link beside the picker.
 *
 * Round 1 ships only one class per category, so the link is hidden in
 * v1 — the conditional gate is wired now so it surfaces automatically
 * when alternates arrive (amsart, IEEEtran, tufte-book, memoir, altacv,
 * etc.).
 */
const CATEGORIES_WITH_ALTERNATES: ReadonlySet<DocumentCategory> = new Set();

/**
 * CategoryTilePicker — the in-scope category tiles (Article / Report).
 *
 * Visual:
 * - ~74px tall, padding 14px 6px 10px, border `--lilia-border`, radius 6px
 * - Hover: `--lilia-hover` background
 * - Selected: `--lilia-primary-subtle` background, `--lilia-primary`
 *   border + text + icon
 *
 * The optional "Change" link below the grid is hidden in v1 (each
 * category has only one canonical class in current scope). The
 * gating logic is in place so the link surfaces automatically once
 * alternates ship — see `CATEGORIES_WITH_ALTERNATES`.
 */
export function CategoryTilePicker(props: CategoryTilePickerProps) {
  const { value, onChange, showChangeLink, onChangeClassClick, currentClass } = props;

  // Offer the in-scope tiles, plus this document's own type if it predates the
  // narrowing. Never offer a descoped type the document does not already have.
  const descopedCurrent = DESCOPED.find((t) => t.category === value);
  const visibleTiles = descopedCurrent ? [...TILES, descopedCurrent] : TILES;

  const categoryHasAlternates = CATEGORIES_WITH_ALTERNATES.has(value);
  const renderChangeLink = Boolean(showChangeLink) && categoryHasAlternates;

  return (
    <div className={styles.root}>
      <div
        className={styles.grid}
        role="radiogroup"
        aria-label={i18n.t("shell:categories.documentCategory")}
        data-testid="category-tile-picker"
      >
        {visibleTiles.map((tile) => {
          const isSelected = tile.category === value;
          const tileClassName = isSelected
            ? `${styles.tile} ${styles.tileSelected}`
            : styles.tile;
          return (
            <button
              key={tile.category}
              type="button"
              role="radio"
              aria-checked={isSelected}
              tabIndex={isSelected ? 0 : -1}
              className={tileClassName}
              data-testid={`category-tile-${tile.category}`}
              onClick={() => onChange(tile.category)}
            >
              <tile.Icon size={18} stroke={1.5} />
              <span className={styles.tileLabel}>{tile.label}</span>
            </button>
          );
        })}
      </div>

      {renderChangeLink && currentClass && (
        <p className={styles.changeRow} data-testid="category-change-row">
          Class: <strong className={styles.classMono}>{currentClass}</strong>
          <span aria-hidden className={styles.changeDot}>
            {" · "}
          </span>
          <button
            type="button"
            className={styles.changeLink}
            onClick={onChangeClassClick}
          >
            Change
          </button>
        </p>
      )}
    </div>
  );
}
